// ---------------------------------------------------------------------------
// PermissionsPage — Tenant Group Roles admin page (Datalake Admin only).
//
// Allows admins to configure which AAD security groups map to the DataCreator
// and DataUser tenant-level roles in the Teams Eval Data Hub.
//
// Access control: reads isAdmin from DataHubPermissionContext. Non-admins see
// an "Access Denied" message (defence-in-depth; the nav item is also hidden).
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Title2,
  Title3,
  Tooltip,
} from '@fluentui/react-components';
import { Info20Regular, LockClosed20Regular } from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TenantRoleSection } from '../../components/Datalake/TenantRoleSection/TenantRoleSection.js';
import {
  PERMISSIONS_ACCESS_DENIED_BODY,
  PERMISSIONS_ACCESS_DENIED_TITLE,
  PERMISSIONS_INFO_TOOLTIP,
  PERMISSIONS_PAGE_DESCRIPTION,
  PERMISSIONS_PAGE_TITLE,
  PERMISSIONS_SAVE_BUTTON,
  TENANT_ROLE_LABEL_DATA_CREATOR,
  TENANT_ROLE_LABEL_DATA_USER,
} from '../../constants/index.js';
import {
  DataHubClientProvider,
  useDataHubClient,
} from '../../context/DataHubClientContext.js';
import {
  DataHubPermissionProvider,
  useDataHubPermission,
} from '../../context/DataHubPermissionContext.js';
import { useTenantGroupRole } from '../../hooks/useTenantGroupRole/useTenantGroupRole.js';
import type { AccessControlGroup } from '../../services/accessControlService.interface.js';
import { AccessControlService } from '../../services/accessControlService.js';
import { withCorrelationId } from '../../services/usageContext.js';
import { useScenarioFactory } from '../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../telemetry/scenarioError.js';
import { ScenarioName } from '../../telemetry/scenarios.js';

import { usePermissionsPageStyles } from './PermissionsPage.style.js';

export function PermissionsPage() {
  return (
    <DataHubPermissionProvider>
      <DataHubClientProvider>
        <PermissionsPageContent />
      </DataHubClientProvider>
    </DataHubPermissionProvider>
  );
}

function PermissionsPageContent() {
  const styles = usePermissionsPageStyles();
  const { isAdmin } = useDataHubPermission();

  if (!isAdmin) {
    return (
      <div className={styles.accessDenied} role="alert">
        <LockClosed20Regular />
        <Title3>{PERMISSIONS_ACCESS_DENIED_TITLE}</Title3>
        <Body1>{PERMISSIONS_ACCESS_DENIED_BODY}</Body1>
      </div>
    );
  }

  return <PermissionsAdminContent />;
}

function PermissionsAdminContent() {
  const styles = usePermissionsPageStyles();
  const { refreshPermissions } = useDataHubPermission();

  const client = useDataHubClient();
  const scenarioFactory = useScenarioFactory();
  const service = useMemo(() => new AccessControlService(client), [client]);

  const {
    dataCreatorGroups,
    dataUserGroups,
    isLoading,
    error,
    save,
    isSaving,
    saveError,
  } = useTenantGroupRole({ service });

  // Local editable copies of the role assignments.
  const [creatorGroups, setCreatorGroups] = useState<AccessControlGroup[]>([]);
  const [userGroups, setUserGroups] = useState<AccessControlGroup[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Sync local state when the hook finishes loading.
  useEffect(() => {
    if (!isLoading) {
      setCreatorGroups(dataCreatorGroups);
      setUserGroups(dataUserGroups);
    }
  }, [isLoading, dataCreatorGroups, dataUserGroups]);

  const handleSave = useCallback(async () => {
    setSaveSuccess(false);
    const scenario = scenarioFactory.newScenario(ScenarioName.PermissionUpdate);
    const scenarioData = { 'Scenario.PermissionScope': 'tenant' };
    try {
      await save(
        {
          dataCreator: creatorGroups.map((g) => g.id),
          dataUser: userGroups.map((g) => g.id),
        },
        { extras: withCorrelationId(scenario.queryId) },
      );
      scenario.complete('success', scenarioData);
      setSaveSuccess(true);
      // Update the nav item in case this is the first save and the user was
      // just granted admin (i.e., the DataHubPermissionContext needs a refresh).
      refreshPermissions();
    } catch (error) {
      const classification = classifyScenarioError(error);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      // saveError is surfaced via the hook's saveError field.
    }
  }, [save, creatorGroups, userGroups, refreshPermissions, scenarioFactory]);

  if (isLoading) {
    return (
      <div className={styles.signIn}>
        <Spinner label="Loading roles..." />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className={styles.root}>
        <MessageBar intent="error">
          <MessageBarBody>{error.message}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Page header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Title2>{PERMISSIONS_PAGE_TITLE}</Title2>
          <Tooltip
            content={PERMISSIONS_INFO_TOOLTIP}
            relationship="description"
            positioning="below-start"
          >
            <Button
              appearance="transparent"
              icon={<Info20Regular />}
              size="small"
              aria-label="Role management information"
              className={styles.infoButton}
            />
          </Tooltip>
        </div>
        <Body1 className={styles.description}>
          {PERMISSIONS_PAGE_DESCRIPTION}
        </Body1>
      </div>

      <div className={styles.content}>
        {/* Data Creator role section */}
        <TenantRoleSection
          label={TENANT_ROLE_LABEL_DATA_CREATOR}
          groups={creatorGroups}
          service={service}
          onGroupAdd={(acg) => setCreatorGroups((prev) => [...prev, acg])}
          onGroupRemove={(id) =>
            setCreatorGroups((prev) => prev.filter((g) => g.id !== id))
          }
        />

        <Divider />

        {/* Data User role section */}
        <TenantRoleSection
          label={TENANT_ROLE_LABEL_DATA_USER}
          groups={userGroups}
          service={service}
          onGroupAdd={(acg) => setUserGroups((prev) => [...prev, acg])}
          onGroupRemove={(id) =>
            setUserGroups((prev) => prev.filter((g) => g.id !== id))
          }
        />

        {/* Save row */}
        <div className={styles.saveRow}>
          <Button
            appearance="primary"
            onClick={() => {
              void handleSave();
            }}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : PERMISSIONS_SAVE_BUTTON}
          </Button>

          {saveError !== null && (
            <span className={styles.saveError} role="alert">
              {saveError.message}
            </span>
          )}

          {saveSuccess && saveError === null && (
            <span className={styles.saveSuccess} aria-live="polite">
              Roles saved successfully.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
